<script lang="ts">
import type { Announcement } from "$lib/notice";
import {
  browserNoticeStorage,
  noticeIsDismissed,
  noticeKeyFor,
  readDismissedKey,
  writeDismissedNotice,
} from "./notice-dismissal";

// The notice above every page, and the read-only bar beside it.
//
// It is SERVER-RENDERED and has to stay useful with no script running at all:
// the whole point of an announcement is that it reaches somebody during the
// incident it is about, which is exactly when a client bundle may be the thing
// that is broken. So the markup is complete from the first byte, the text is
// interpolated as text (never as markup — the string comes from a flag an
// operator typed), and JavaScript only ever REMOVES it.
//
// Dismissal is per session and per notice: the key carries the text, so a new
// notice is shown again to somebody who dismissed the last one. `sessionStorage`
// can throw or come back empty (a private window, blocked site data), so every
// read and write is guarded and the notice simply stays up when it does.
//
// THE ANNOUNCEMENT ARRIVES AFTER THIS COMPONENT DOES. A page built at build
// time carries no notice, so the layout asks the browser for one and hands it
// down once it answers (routes/+layout.svelte). A one-shot read at mount
// therefore ran while there was nothing to look up and never ran again, and the
// dismissal did not hold on any prerendered page: the banner came back on every
// load, for good. The read is keyed on the notice's own storage key instead, so
// it happens whenever a notice becomes known and again if a different one
// replaces it.
//
// WHAT IS DISMISSED IS A KEY, NOT A BOOLEAN. Holding the key that was dismissed
// rather than a flag is what makes a NEW notice visible to somebody who
// dismissed the last one — a flag would have to be cleared, and clearing it is
// a second thing that can be got wrong. It also means the read below only ever
// sets the key it just found in storage: it never clears one, so a dismissal
// clicked in the same tick as the read cannot be undone by it.

let {
  announcement = null,
  readOnly = false,
}: { announcement?: Announcement | null; readOnly?: boolean } = $props();

const storageKey = $derived(announcement ? noticeKeyFor(announcement.text) : null);

// The key this session has put away, if any. Never cleared here: a notice stops
// being dismissed by being replaced with a different one, which gives a
// different key.
let dismissedKey = $state<string | null>(null);

const dismissed = $derived(noticeIsDismissed(storageKey, dismissedKey));

$effect(() => {
  // Depends on the key alone, so it runs when a notice first becomes known —
  // which on a prerendered page is after the layout has fetched it — and again
  // when a different notice replaces it. It does not read `dismissedKey`, so
  // writing it here cannot re-trigger this.
  const found = readDismissedKey(storageKey, browserNoticeStorage());
  if (found !== null) dismissedKey = found;
});

function dismiss(): void {
  // The TEXT is what goes down, not the key: the key belongs to
  // notice-dismissal.ts and is derived there from exactly this value.
  const text = announcement?.text;
  if (text === undefined) return;
  dismissedKey = noticeKeyFor(text);
  writeDismissedNotice(text, browserNoticeStorage());
}

const TONE: Record<string, string> = {
  info: "bg-sky-50 text-sky-900 border-sky-200",
  warning: "bg-amber-50 text-amber-900 border-amber-200",
  critical: "bg-rose-50 text-rose-900 border-rose-200",
};
</script>

{#if readOnly}
  <div
    class="border-b border-amber-200 bg-amber-50 text-amber-900"
    role="status"
    data-testid="read-only-notice"
  >
    <div class="mx-auto max-w-6xl px-4 py-2 text-sm">
      <strong class="font-semibold">Maintenance in progress.</strong>
      The service is read-only right now: you can see everything, and nothing can be changed or
      submitted until it is back. Nothing has been charged.
      <a href="/status" class="font-medium underline" data-testid="read-only-status">
        See our status page
      </a>.
    </div>
  </div>
{/if}

{#if announcement && !dismissed}
  <div
    class={`border-b ${TONE[announcement.level] ?? TONE.info}`}
    role="status"
    data-testid="announcement"
  >
    <div class="mx-auto flex max-w-6xl items-start gap-4 px-4 py-2 text-sm">
      <p class="flex-1" data-testid="announcement-text">{announcement.text}</p>
      <button
        type="button"
        class="shrink-0 rounded px-2 py-0.5 text-xs font-medium underline"
        onclick={dismiss}
        data-testid="announcement-dismiss"
        aria-label="Dismiss this announcement"
      >
        Dismiss
      </button>
    </div>
  </div>
{/if}
